import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { V4AlertCard } from './v4/V4AlertCard';

/**
 * 📱 Active Notifications Component (Feb 21, 2026)
 * 
 * Simplified view of actionable trading alerts for regular users.
 * Shows:
 * - LIVE P&L Summary at top (Feb 21, 2026)
 * - Pending setups (waiting for better entry)
 * - Recent notifications (active trades from last 24h)
 * 
 * Each alert displays:
 * - Scanner emoji and name
 * - Recommended order type (Market/Limit/Stop)
 * - Trading pair and direction
 * - Entry, SL, TP details
 * - ACTIVATE button with risk input (Feb 21, 2026)
 */
const ActiveNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sentCount, setSentCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'sent', 'activated'
  const [activatedCount, setActivatedCount] = useState(0);
  
  // Risk input state for each notification (keyed by notification id)
  const [riskInputs, setRiskInputs] = useState({});
  const [activatingId, setActivatingId] = useState(null);
  
  // Live P&L state (Feb 21, 2026)
  const [liveTrades, setLiveTrades] = useState([]);
  const [livePnLSummary, setLivePnLSummary] = useState({
    active_trades: 0,
    total_pnl: 0,
    total_risk: 0,
    winning: 0,
    losing: 0
  });

  // Fetch activated trades for Live P&L
  const fetchLiveTrades = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/activated-trades`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Filter only ACTIVE trades (not closed)
        const activeTrades = (data.trades || []).filter(t => t.status === 'ACTIVE');
        setLiveTrades(activeTrades);
        setLivePnLSummary({
          active_trades: activeTrades.length,
          total_pnl: activeTrades.reduce((sum, t) => sum + (t.current_pnl || 0), 0),
          total_risk: activeTrades.reduce((sum, t) => sum + (t.risk_amount || 0), 0),
          winning: activeTrades.filter(t => (t.current_pnl || 0) > 0).length,
          losing: activeTrades.filter(t => (t.current_pnl || 0) < 0).length
        });
      }
    } catch (err) {
      console.error('Error fetching live trades:', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/user/v4-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const list = data.notifications || [];
        setNotifications(list);
        setSentCount(list.filter((n) => !n.trade_open).length);
        setActivatedCount(data.open_count || 0);
        setError(null);
      } else {
        setError(data.error || 'Failed to fetch notifications');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Activate a trade
  const handleActivateTrade = async (notif) => {
    const riskAmount = parseFloat(riskInputs[notif.id] || 0);
    
    if (!riskAmount || riskAmount <= 0) {
      alert('Please enter a valid risk amount (e.g., $50)');
      return;
    }
    
    setActivatingId(notif.id);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/activate-trade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notification_id: notif.id,
          pair: notif.pair,
          direction: notif.direction,
          entry_price: notif.entry_price || notif.current_price,
          sl_price: notif.sl_price,
          tp_price: notif.tp_price,
          risk_amount: riskAmount,
          scanner: notif.scanner,
          scanner_name: notif.scanner_name,
          from_queue: notif.from_queue || false
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Trade Activated!\n\n${notif.pair} ${notif.direction}\nRisk: $${riskAmount}\n\nYour trade is now being tracked. Check Trade Performance for results.`);
        // Refresh to show updated status
        fetchNotifications();
        fetchLiveTrades();
        // Clear the risk input
        setRiskInputs(prev => ({ ...prev, [notif.id]: '' }));
      } else {
        alert(`❌ Activation Failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error activating trade:', err);
      alert('❌ Error activating trade. Please try again.');
    } finally {
      setActivatingId(null);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchLiveTrades();
    // Refresh every 60 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchLiveTrades();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'sent') return !n.trade_open;
    if (filter === 'activated') return n.trade_open === true;
    return true;
  });

  const formatPrice = (price, pair) => {
    if (!price) return '-';
    const decimals = pair?.includes('JPY') ? 3 : 5;
    return price.toFixed(decimals);
  };

  const formatAge = (minutes) => {
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
    return `${Math.floor(minutes / 1440)}d ago`;
  };

  const getOrderTypeStyle = (orderType) => {
    switch (orderType) {
      case 'MARKET':
        return { bg: '#dcfce7', color: '#16a34a', border: '#22c55e' };
      case 'LIMIT':
        return { bg: '#dbeafe', color: '#1d4ed8', border: '#3b82f6' };
      case 'STOP':
        return { bg: '#fef3c7', color: '#b45309', border: '#f59e0b' };
      default:
        return { bg: '#f1f5f9', color: '#475569', border: '#94a3b8' };
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
        Loading active notifications...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#dc2626' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        {error}
        <button 
          onClick={fetchNotifications}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: '#0891b2',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="active-notifications">
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* 💰 LIVE P&L SUMMARY (Feb 21, 2026)                                      */}
      {/* Shows at the top when user has active trades                            */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {liveTrades.length > 0 && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1.25rem',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: '16px',
          border: '2px solid #0891b2',
          boxShadow: '0 4px 20px rgba(8, 145, 178, 0.3)'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              color: 'white', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💰 MY LIVE TRADES
            </h3>
            <button
              onClick={() => { fetchLiveTrades(); fetchNotifications(); }}
              style={{
                padding: '0.35rem 0.75rem',
                background: 'rgba(255,255,255,0.1)',
                color: '#94a3b8',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh
            </button>
          </div>
          
          {/* Summary Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            {/* Active Trades */}
            <div style={{
              padding: '0.75rem',
              background: 'rgba(59, 130, 246, 0.2)',
              borderRadius: '10px',
              textAlign: 'center',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <div style={{ fontSize: '0.7rem', color: '#93c5fd', marginBottom: '0.25rem', fontWeight: '600' }}>
                ACTIVE TRADES
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#60a5fa' }}>
                {livePnLSummary.active_trades}
              </div>
            </div>
            
            {/* Live P&L */}
            <div style={{
              padding: '0.75rem',
              background: livePnLSummary.total_pnl >= 0 
                ? 'rgba(34, 197, 94, 0.2)' 
                : 'rgba(239, 68, 68, 0.2)',
              borderRadius: '10px',
              textAlign: 'center',
              border: `1px solid ${livePnLSummary.total_pnl >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
            }}>
              <div style={{ fontSize: '0.7rem', color: '#d1d5db', marginBottom: '0.25rem', fontWeight: '600' }}>
                LIVE P&L
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700', 
                color: livePnLSummary.total_pnl >= 0 ? '#4ade80' : '#f87171'
              }}>
                {livePnLSummary.total_pnl >= 0 ? '+' : ''}${livePnLSummary.total_pnl.toFixed(2)}
              </div>
            </div>
            
            {/* ROI */}
            <div style={{
              padding: '0.75rem',
              background: 'rgba(139, 92, 246, 0.2)',
              borderRadius: '10px',
              textAlign: 'center',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}>
              <div style={{ fontSize: '0.7rem', color: '#c4b5fd', marginBottom: '0.25rem', fontWeight: '600' }}>
                TOTAL RISK
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#a78bfa' }}>
                ${livePnLSummary.total_risk.toFixed(0)}
              </div>
            </div>
          </div>
          
          {/* Individual Trade Breakdown */}
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
            padding: '0.75rem'
          }}>
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#94a3b8', 
              marginBottom: '0.5rem',
              fontWeight: '600'
            }}>
              TRADE BREAKDOWN:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {liveTrades.map((trade, idx) => (
                <div 
                  key={trade.activation_id || idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  {/* Pair & Direction */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      fontWeight: '700', 
                      color: 'white',
                      fontSize: '0.9rem'
                    }}>
                      {trade.pair}
                    </span>
                    <span style={{
                      padding: '0.15rem 0.5rem',
                      background: trade.direction === 'BUY' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                      color: trade.direction === 'BUY' ? '#4ade80' : '#f87171',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      {trade.direction}
                    </span>
                  </div>
                  
                  {/* Risk */}
                  <span style={{ 
                    color: '#94a3b8', 
                    fontSize: '0.8rem'
                  }}>
                    ${trade.risk_amount?.toFixed(0)} risk
                  </span>
                  
                  {/* P&L */}
                  <span style={{
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    color: (trade.current_pnl || 0) >= 0 ? '#4ade80' : '#f87171',
                    minWidth: '80px',
                    textAlign: 'right'
                  }}>
                    {(trade.current_pnl || 0) >= 0 ? '+' : ''}${(trade.current_pnl || 0).toFixed(2)}
                    {(trade.current_pnl || 0) >= 0 ? ' ✅' : ' 📉'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Tip */}
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: 'rgba(251, 191, 36, 0.1)',
            borderRadius: '8px',
            border: '1px dashed rgba(251, 191, 36, 0.3)',
            fontSize: '0.75rem',
            color: '#fbbf24'
          }}>
            💡 To close a trade: Go to <strong>Calculator</strong> → <strong>My Positions</strong> → <strong>Live</strong> → press <strong>Close</strong>
          </div>
        </div>
      )}

      {/* Header with counts */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0891b2' }}>
          📱 Active Notifications
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>
          💀 V4 trade alerts. Each alert disappears after 60 minutes unless you place the trade.
        </p>
        
        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            data-testid="filter-all"
            onClick={() => setFilter('all')}
            style={{
              padding: '0.5rem 1rem',
              background: filter === 'all' ? '#0891b2' : '#f1f5f9',
              color: filter === 'all' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.85rem'
            }}
          >
            All ({notifications.length})
          </button>
          <button
            data-testid="filter-sent"
            onClick={() => setFilter('sent')}
            style={{
              padding: '0.5rem 1rem',
              background: filter === 'sent' ? '#3b82f6' : '#f1f5f9',
              color: filter === 'sent' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.85rem'
            }}
          >
            📨 Ready ({sentCount})
          </button>
          <button
            data-testid="filter-activated"
            onClick={() => setFilter('activated')}
            style={{
              padding: '0.5rem 1rem',
              background: filter === 'activated' ? '#22c55e' : '#f1f5f9',
              color: filter === 'activated' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.85rem'
            }}
          >
            ✅ Traded ({activatedCount})
          </button>
        </div>
      </div>

      {/* Refresh Button */}
      <button
        data-testid="refresh-notifications"
        onClick={fetchNotifications}
        style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: '#f1f5f9',
          color: '#475569',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '0.85rem'
        }}
      >
        🔄 Refresh
      </button>

      {/* Empty State */}
      {filteredNotifications.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '2px dashed #e2e8f0'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
          <div style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: '0.5rem' }}>
            {filter === 'activated' ? 'No open trades from alerts' : 'No active alerts'}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#94a3b8' }} data-testid="notifications-empty">
            {filter === 'activated'
              ? 'Trades you place from an alert show here until they close.'
              : 'No 💀 V4 alerts in the last 60 minutes. New ones appear here instantly.'
            }
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {filteredNotifications.map((notif, idx) => (
          <V4AlertCard key={notif.id || idx} notif={notif} idx={idx}
            onTrade={(n) => navigate(`/calculator?n=${encodeURIComponent(n.id)}&pair=${n.pair}&dir=${n.direction}`)} />
        ))}
      </div>

    </div>
  );
};

export default ActiveNotifications;
